import React from 'react';
import { Button } from './ui/button';
import { Sparkles, ArrowRight } from 'lucide-react';
import {
  motion,
  useScroll,
  useTransform,
  FloatingElement,
  MagneticButton,
  TextReveal
} from './animations';

const Hero = () => {
  const { scrollY } = useScroll();

  // Parallax effects
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150]);
  const imageScale = useTransform(scrollY, [0, 300], [1, 1.1]);
  const textY = useTransform(scrollY, [0, 300], [0, 50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.3]);

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, x: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1],
        delay: 0.3
      }
    }
  };

  return (
    <section className="relative min-h-[95vh] flex items-center overflow-visible bg-gradient-subtle pt-20 pb-24">

      {/* Animated Background Elements with Parallax */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ y: backgroundY }}
      >
        <motion.div
          className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-primary/10 blur-[100px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-[10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-secondary/10 blur-[80px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        {/* Extra floating orbs */}
        <motion.div
          className="absolute top-[40%] left-[20%] w-[200px] h-[200px] rounded-full bg-accent/5 blur-[60px]"
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left Column: Text Content with Stagger Animations */}
          <motion.div
            className="space-y-8 max-w-2xl"
            style={{ y: textY, opacity }}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Badge */}
            <motion.div variants={itemVariants}>
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-soft border border-primary/20"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 30px -10px rgba(244, 143, 177, 0.4)" }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Sparkles className="w-4 h-4 text-primary" />
                </motion.div>
                <span className="text-sm font-medium text-foreground tracking-wide">Medicina Estetica de Vanguardia</span>
              </motion.div>
            </motion.div>

            {/* Headline with Text Reveal */}
            <motion.h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-[1.1]"
              variants={itemVariants}
            >
              <TextReveal text="Tu belleza" type="words" />
              <br />
              <motion.span
                className="font-serif italic text-primary font-light inline-block"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                autentica
              </motion.span>
              , <br />
              <TextReveal text="potenciada." type="words" delay={0.3} />
            </motion.h1>

            <motion.p
              className="text-lg text-muted-foreground leading-relaxed max-w-lg"
              variants={itemVariants}
            >
              Descubre la armonia perfecta entre ciencia medica y arte estetico. Tratamientos personalizados para resultados naturales y duraderos.
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 pt-4"
              variants={itemVariants}
            >
              <MagneticButton strength={0.2}>
                <Button
                  onClick={scrollToForm}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 shadow-elegant hover:shadow-glow transition-all duration-300 relative overflow-hidden group"
                >
                  <motion.span
                    className="relative z-10 font-semibold"
                    whileHover={{ scale: 1.05 }}
                  >
                    Reservar Cita
                  </motion.span>
                  <motion.div
                    className="absolute inset-0 bg-white/20"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  />
                </Button>
              </MagneticButton>

              <motion.div
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Button
                  onClick={() => window.open('https://wa.me/34637285137', '_blank')}
                  variant="ghost"
                  size="lg"
                  className="text-foreground hover:text-primary hover:bg-transparent px-6 py-6 group"
                >
                  <span className="font-medium">Consultar por WhatsApp</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </motion.div>
                </Button>
              </motion.div>
            </motion.div>

            {/* Stats / Trust with Animated Numbers */}
            <motion.div
              className="flex items-center gap-8 pt-8 border-t border-primary/10"
              variants={itemVariants}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.5, type: "spring" }}
              >
                <motion.p
                  className="text-3xl font-serif font-bold text-primary"
                  whileHover={{ scale: 1.1 }}
                >
                  10+
                </motion.p>
                <p className="text-sm text-muted-foreground">Anos de experiencia</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2, duration: 0.5, type: "spring" }}
              >
                <motion.p
                  className="text-3xl font-serif font-bold text-primary"
                  whileHover={{ scale: 1.1 }}
                >
                  3
                </motion.p>
                <p className="text-sm text-muted-foreground">Sedes en Valencia</p>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Column: Hero Image with Organic Mask */}
          <motion.div
            className="relative h-[600px] lg:h-[700px] hidden lg:block"
            variants={imageVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Main Image Masked with Parallax Scale */}
            <motion.div
              className="absolute inset-0 z-10 overflow-hidden rounded-[40px] rounded-tr-[120px] rounded-bl-[120px] shadow-2xl"
              style={{ scale: imageScale }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
                <img
                src="/hero-model.jpg"
                alt="Medicina Estetica"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </motion.div>

            {/* Floating Elements with Enhanced Animations */}
            <motion.div
              className="absolute -right-8 top-20 z-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Floating Card with Smooth Animation */}
            <FloatingElement duration={4} distance={10} className="absolute top-[480px] -left-4 z-20">
              <motion.div
                className="bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-elegant"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 20px 40px -10px rgba(244, 143, 177, 0.3)"
                }}
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <Sparkles className="w-5 h-5 text-green-600" />
                  </motion.div>
                  <div>
                    <p className="text-xs font-bold text-foreground">Resultados Naturales</p>
                    <p className="text-[10px] text-muted-foreground">100% Satisfaccion</p>
                  </div>
                </div>
              </motion.div>
            </FloatingElement>

            {/* New floating element - top right */}
            <FloatingElement duration={5} distance={12} delay={1} className="absolute top-32 -right-4 z-20">
              <motion.div
                className="bg-white/90 backdrop-blur-md px-4 py-3 rounded-xl shadow-elegant"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <motion.div
                      className="w-6 h-6 rounded-full bg-primary/20 border-2 border-white"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div
                      className="w-6 h-6 rounded-full bg-secondary/30 border-2 border-white"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                    />
                    <motion.div
                      className="w-6 h-6 rounded-full bg-accent/30 border-2 border-white"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                    />
                  </div>
                  <p className="text-xs font-medium text-foreground">+5000 clientas</p>
                </div>
              </motion.div>
            </FloatingElement>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-muted-foreground cursor-pointer"
          onClick={() => {
            const servicesSection = document.querySelector('section:nth-of-type(2)');
            if (servicesSection) servicesSection.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <span className="text-xs font-medium">Descubre mas</span>
          <motion.div
            className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center pt-2"
          >
            <motion.div
              className="w-1.5 h-3 bg-primary/50 rounded-full"
              animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
